import { useEffect, useRef } from 'react';
import { resolveActiveContext } from './contextResolution';
import type { AuthSession } from '@/auth/types';

type UseAppRoutingArgs = {
  authLoading: boolean;
  bootstrapLoaded: boolean;
  session: AuthSession | null;
  pathname: string;
  organisations: Array<{ id: string; slug: string }>;
  projects: Array<{ id: string; slug: string; organisationId?: string | null }>;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  setSelectedOrganisationId: (id: string | null) => void;
  setSelectedProjectId: (id: string | null) => void;
  navigate: (path: string, options?: { replace?: boolean }) => void;
  updateUserContext: (input: {
    lastOrganisationId?: string | null;
    lastProjectId?: string | null;
  }) => void;
  accessToken: string | null;
  allowOrganisationRouting?: boolean;
};

export const useAppRouting = ({
  authLoading,
  bootstrapLoaded,
  session,
  pathname,
  organisations,
  projects,
  selectedOrganisationId,
  selectedProjectId,
  setSelectedOrganisationId,
  setSelectedProjectId,
  navigate,
  updateUserContext,
  accessToken,
  allowOrganisationRouting = true,
}: UseAppRoutingArgs): void => {
  const didRestoreContext = useRef(false);
  const previousUserId = useRef<string | null>(null);
  const lastContextSent = useRef<{ orgId: string | null; projectId: string | null } | null>(null);
  const lastResolvedReasonsRef = useRef<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!session) return;
    if (typeof window === 'undefined') return;
    const resolution = resolveActiveContext({
      pathname,
      session,
      organisations,
      projects,
      persistedOrganisationId: selectedOrganisationId,
      persistedProjectId: selectedProjectId,
    });
    const reasonKey = `${resolution.reasonCodes.organisation}:${resolution.reasonCodes.project}:${resolution.activeOrganisationId ?? 'none'}:${resolution.activeProjectId ?? 'none'}`;
    if (lastResolvedReasonsRef.current === reasonKey) return;
    lastResolvedReasonsRef.current = reasonKey;
    window.dispatchEvent(
      new CustomEvent('flowqa-context-resolution', {
        detail: {
          resolvedFromOrganisation: resolution.reasonCodes.organisation,
          resolvedFromProject: resolution.reasonCodes.project,
          activeOrganisationId: resolution.activeOrganisationId,
          activeProjectId: resolution.activeProjectId,
          pathname,
        },
      }),
    );
  }, [
    authLoading,
    organisations,
    pathname,
    projects,
    selectedOrganisationId,
    selectedProjectId,
    session,
  ]);

  useEffect(() => {
    if (authLoading) return;
    if (!session) return;
    if (!bootstrapLoaded) return;
    if (pathname !== '/') return;
    const resolution = resolveActiveContext({
      pathname,
      session,
      organisations,
      projects,
      persistedOrganisationId: selectedOrganisationId,
      persistedProjectId: selectedProjectId,
    });
    const preferredProjectId = resolution.activeProjectId;
    if (preferredProjectId || allowOrganisationRouting || organisations.length > 0) {
      navigate('/execution-runs', { replace: true });
    }
  }, [
    authLoading,
    bootstrapLoaded,
    allowOrganisationRouting,
    organisations,
    pathname,
    selectedOrganisationId,
    selectedProjectId,
    session,
    projects,
    navigate,
  ]);

  useEffect(() => {
    if (authLoading) return;
    if (!session) return;
    if (!bootstrapLoaded) return;
    if (didRestoreContext.current) return;
    const resolution = resolveActiveContext({
      pathname,
      session,
      organisations,
      projects,
      persistedOrganisationId: selectedOrganisationId,
      persistedProjectId: selectedProjectId,
    });
    const nextOrg = resolution.activeOrganisationId;
    const nextProject = resolution.activeProjectId;
    if (nextOrg && nextOrg !== selectedOrganisationId) {
      setSelectedOrganisationId(nextOrg);
    }
    if (nextOrg === null && selectedOrganisationId !== null) setSelectedOrganisationId(null);
    if (nextProject && nextProject !== selectedProjectId) setSelectedProjectId(nextProject);
    if (nextProject === null && selectedProjectId !== null) setSelectedProjectId(null);
    didRestoreContext.current = true;
  }, [
    authLoading,
    bootstrapLoaded,
    organisations,
    pathname,
    projects,
    selectedOrganisationId,
    selectedProjectId,
    session,
    setSelectedOrganisationId,
    setSelectedProjectId,
  ]);

  useEffect(() => {
    if (authLoading) return;
    const currentUserId = session?.userId ?? null;
    if (previousUserId.current === currentUserId) return;
    previousUserId.current = currentUserId;
    didRestoreContext.current = false;
    lastContextSent.current = null;
    if (!session) {
      setSelectedOrganisationId(null);
      setSelectedProjectId(null);
      return;
    }
    if (!bootstrapLoaded) {
      return;
    }
    const resolution = resolveActiveContext({
      pathname,
      session,
      organisations,
      projects,
      persistedOrganisationId: selectedOrganisationId,
      persistedProjectId: selectedProjectId,
    });
    setSelectedOrganisationId(resolution.activeOrganisationId);
    setSelectedProjectId(resolution.activeProjectId);
  }, [
    authLoading,
    bootstrapLoaded,
    organisations,
    pathname,
    projects,
    session,
    selectedOrganisationId,
    selectedProjectId,
    setSelectedOrganisationId,
    setSelectedProjectId,
  ]);

  useEffect(() => {
    if (authLoading) return;
    if (!accessToken) return;
    if (!didRestoreContext.current) return;
    const orgId = selectedOrganisationId ?? null;
    const projectId = selectedProjectId ?? null;
    const lastSent = lastContextSent.current;
    if (lastSent?.orgId === orgId && lastSent.projectId === projectId) {
      return;
    }
    updateUserContext({ lastOrganisationId: orgId, lastProjectId: projectId });
    lastContextSent.current = { orgId, projectId };
  }, [accessToken, authLoading, selectedOrganisationId, selectedProjectId, updateUserContext]);
};
